import { useEffect, useState } from "react"
import { api, urls } from '../../../services/api'
import { INews } from '../../../types/news'
import { SideTopNews } from "./components/TopNews/SideNews"
import { BottomNews } from "./components/TopNews/BottomNews"
import { ListBottomNews } from "./components/ListTopNews/ListBottomNews"
import { ListSideNews } from "./components/ListTopNews/ListSideNews"
import { MainNews } from "./components/TopNews/MainNews"
import { Subcontainer } from "./components/Containers/Subcontainer"
import { useApp } from '../../../context/App'
import { CardMobileNews } from "./components/TopNews/CardMobileNews"
import { BottomNewsMobile } from "./components/TopNews/BottomNewsMobile"
import { SectionTitle } from "../../../components/ui/SectionTitle"
import { Section } from "../../../components/ui/Section"
import { Button } from "../../../components/ui/Button"
import { ChevronRight, Newspaper } from 'lucide-react'
import { Skeleton, NewsCardSkeleton } from "../../../components/ui/Skeleton"

export function News() {
	const [news, setNews] = useState<INews[]>([])
	const [getPeriodoEleitoral, setPeriodoEleitoral] = useState()
	const [loading, setLoading] = useState(true)
	const [activeSlide, setActiveSlide] = useState(0)
	const { mobile } = useApp()

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const container = e.currentTarget
		const scrollPosition = container.scrollLeft
		const itemWidth = container.offsetWidth * 0.82
		const newActiveSlide = Math.round(scrollPosition / itemWidth)
		if (newActiveSlide !== activeSlide) setActiveSlide(newActiveSlide)
	}

	useEffect(() => {
		(async () => {
			try {
				const response = await api.get(urls.ListNewsDestaquesHome)
				setNews(response.data.noticias)
				setPeriodoEleitoral(response.data.periodoEleitoral)
			} catch (error) {
				 // console.error('News fetch error:', error)
			} finally {
				setLoading(false)
			}
		})()
	}, [])

	if (loading) {
		return (
			<Section>
				<div className="w-full flex items-center justify-center gap-6 lg:gap-8 mb-8">
					<div className="hidden lg:block flex-1 h-px bg-border-default/20" />
					<Skeleton className="h-8 w-48" />
					<div className="flex-1 h-px bg-border-default/20" />
				</div>
				<div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
					<div className="lg:col-span-7">
						<NewsCardSkeleton />
					</div>
					<div className="lg:col-span-5 flex flex-col gap-6">
						<Skeleton className="flex-1 rounded-2xl h-40" />
						<Skeleton className="flex-1 rounded-2xl h-40" />
					</div>
				</div>
			</Section>
		);
	}

	if (news.length < 5 && getPeriodoEleitoral === 'Não') {
		return null
	}

	if (mobile) {
		return (
			<>
				{news.length > 0 && (
					<Section>
						<SectionTitle
							title="Últimas Notícias"
							icon={<Newspaper className="text-secondary" size={24} />}
						/>

						{getPeriodoEleitoral === 'Não' && (
							<div className="relative">
								<div
									onScroll={handleScroll}
									className="flex overflow-x-auto gap-5 pb-6 -mx-4 px-4 snap-x scroll-smooth no-scrollbar custom-scrollbar-hide"
								>
									{news.slice(0, 5).map((item, idx) => (
										<div key={idx} className="min-w-[82%] sm:min-w-[70%] snap-center">
											<CardMobileNews news={item} isHero />
										</div>
									))}
								</div>
								{/* Visual Hint for Scroll */}
								<div className="flex justify-center gap-1.5 mt-2 mb-6">
									{news.slice(0, 5).map((_, i) => (
										<div
											key={i}
											className={`h-1.5 rounded-full transition-all duration-300 ${i === activeSlide ? 'w-6 bg-secondary' : 'w-2 bg-border-default dark:bg-white/10'}`}
										/>
									))}
								</div>
							</div>
						)}

						<div className="flex flex-col gap-6">
							{news[0] && getPeriodoEleitoral === 'Sim' && <BottomNewsMobile news={news[0]} />}
							{news[1] && getPeriodoEleitoral === 'Sim' && <BottomNewsMobile news={news[1]} />}
							{news[2] && getPeriodoEleitoral === 'Sim' && <BottomNewsMobile news={news[2]} />}

							{/* Rest of the news as a list, starting from index 5 if carousel active, or 3 if electoral */}
							{news.slice(getPeriodoEleitoral === 'Não' ? 5 : 3, 11).map((item, idx) => (
								<BottomNewsMobile key={idx} news={item} />
							))}
						</div>

						<a
							href="/noticias"
							className="flex items-center justify-center gap-2 w-full py-4 bg-primary dark:bg-primary-light text-white rounded-xl mt-8 mb-4 font-semibold text-sm uppercase tracking-wider no-underline transition-all duration-200 hover:bg-primary-dark dark:hover:bg-primary active:scale-95"
						>
							Mais Notícias
							<ChevronRight size={16} />
						</a>
					</Section>
				)}
			</>
		)
	}

	return (
		<Section>
			{news.length > 0 && (
				<>

					<SectionTitle
						title="Últimas Notícias"
						icon={<Newspaper className="text-secondary" size={24} />}
					/>

					{getPeriodoEleitoral === 'Não' && (
						<Subcontainer>
							{news[0] && <MainNews news={news[0]} />}
							<ListSideNews>
								{news[1] && <SideTopNews news={news[1]} />}
								{news[2] && <SideTopNews news={news[2]} />}
							</ListSideNews>
						</Subcontainer>
					)}

					<ListBottomNews>
						{news[0] && getPeriodoEleitoral === 'Sim' && <BottomNews news={news[0]} />}
						{news[1] && getPeriodoEleitoral === 'Sim' && <BottomNews news={news[1]} />}
						{news[2] && getPeriodoEleitoral === 'Sim' && <BottomNews news={news[2]} />}
						{news.slice(3, 11).map((item, idx) => (
							<BottomNews key={idx} news={item} />
						))}
					</ListBottomNews>

					<Button
						variant="primary"
						size="lg"
						className="w-full flex items-center justify-center gap-2 group mb-8"
						onClick={() => window.location.href = '/noticias'}
					>
						<span>Mais Notícias</span>
						<ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
					</Button>
				</>
			)}
		</Section>
	)
}